// src/ui/components/UserMenu.js
// Controlador para el área de usuario y dropdown de configuración/notificaciones.

import { getSupabaseClient, signOut } from "../../services/supabaseClient.js";
import { getCurrentUserAndProfile, getCurrentTeamId } from "../../services/teamService.js";
import { actions, subscribe } from "../../app/state.js";
import { ROLE_LABELS } from "../../domain/constants.js";
import { mountRoute, refreshActiveNav } from "../../app/router.js";
import { listUnreadEmailsByTeam, markEmailAsRead } from "../../services/emailsService.js";
import { listPendingActivitiesByTeam } from "../../services/activitiesService.js";
import { openEditEventModal } from "./EventModal.js";
import { openProfileModal } from "./ProfileModal.js";
import { openUserManagementModal } from "./UserManagementModal.js";
import { openEditActivityModal } from "./ActivityModal.js";
import { getUserRole } from "../../services/rbac.js";

function $(root, sel) { return root.querySelector(sel); }

export async function mountUserMenu(container) {
    if (!container) return;

    const template = document.getElementById("user-menu-template");
    if (!template) return;

    const content = template.content.cloneNode(true);
    const root = content.firstElementChild;
    container.innerHTML = ""; // Clear "Cargando..." state
    container.appendChild(root);

    // --- Wire Interaction: Profile Dropdown ---
    const trigger = $(root, "#user-menu-trigger");
    const dropdown = $(root, "#user-menu-dropdown");

    trigger.onclick = (e) => {
        e.stopPropagation();
        closeDropdown($(root, "#user-notif-dropdown")); // Close other dropdowns
        const isHidden = dropdown.classList.contains("hidden");
        if (isHidden) openDropdown(dropdown);
        else closeDropdown(dropdown);
    };

    // --- Wire Interaction: Notifications ---
    const notifBtn = $(root, "#user-notif-btn");
    const notifDropdown = $(root, "#user-notif-dropdown");

    notifBtn.onclick = (e) => {
        e.stopPropagation();
        closeDropdown(dropdown); // Close profile dropdown
        const isHidden = notifDropdown.classList.contains("hidden");
        if (isHidden) {
            openDropdown(notifDropdown);
            refreshNotifications(root);
        } else {
            closeDropdown(notifDropdown);
        }
    };

    // Close when clicking outside
    document.addEventListener("click", () => {
        closeDropdown(dropdown);
        closeDropdown(notifDropdown);
    });

    // Prevent closing when clicking inside
    dropdown.onclick = (e) => e.stopPropagation();
    notifDropdown.onclick = (e) => e.stopPropagation();

    // --- Profile Settings Button ---
    const profileSettingsBtn = $(root, "#btn-profile-settings");
    if (profileSettingsBtn) {
        profileSettingsBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            closeDropdown(dropdown);
            mountRoute("settings");
        };
    }

    // --- Admin Panel Buttons ---
    const adminTeamBtn = $(root, "#btn-admin-team");
    if (adminTeamBtn) {
        adminTeamBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            closeDropdown(dropdown);
            openUserManagementModal();
        };
    }

    const darkModeBtn = $(root, "#dark-mode-toggle");
    darkModeBtn.onclick = () => {
        const isDark = document.documentElement.classList.toggle("dark");
        const thumb = $(root, "#dark-mode-toggle-thumb");
        const icon = $(root, "#theme-icon");

        if (isDark) {
            darkModeBtn.classList.replace("bg-slate-200", "bg-slate-900");
            thumb.style.transform = "translateX(18px)";
            icon.className = "fa-solid fa-sun text-amber-400";
            actions.addToast?.({ type: "info", message: "Tema oscuro activado (VISTA PREVIA)" });
        } else {
            darkModeBtn.classList.replace("bg-slate-900", "bg-slate-200");
            thumb.style.transform = "translateX(0px)";
            icon.className = "fa-solid fa-moon text-slate-400";
            actions.addToast?.({ type: "info", message: "Tema claro activado" });
        }
    };

    const signOutBtn = $(root, "#btn-sign-out");
    signOutBtn.onclick = async () => {
        try {
            if (confirm("¿Estás seguro de que quieres cerrar sesión?")) {
                await signOut();
                window.location.reload();
            }
        } catch (e) {
            actions.addToast?.({ type: "error", message: "Error al cerrar sesión" });
        }
    };

    // --- Init & Sync ---
    syncUserData(root);
    refreshNotifications(root, { silent: true });

    // Polling notifications every 2 minutes
    const interval = setInterval(() => refreshNotifications(root, { silent: true }), 120000);

    // Clean up
    root.addEventListener("remove", () => clearInterval(interval));

    subscribe((state, prev) => {
        if (state.team.profile !== prev.team.profile) {
            syncUserData(root);
            refreshNotifications(root);
        }
    });

    // Mark all read listener
    $(root, "#notif-mark-read").onclick = async (e) => {
        e.stopPropagation();
        try {
            const teamId = await getCurrentTeamId();
            if (!teamId) return;

            const mails = await listUnreadEmailsByTeam(teamId);
            if (mails.length > 0) {
                await Promise.all(mails.map(m => markEmailAsRead(m.id)));
                actions.addToast?.({ type: "success", message: "Emails marcados como leídos" });
                refreshNotifications(root);
            } else {
                actions.addToast?.({ type: "info", message: "No hay emails pendientes del equipo" });
            }
        } catch (err) {
            console.error(err);
            actions.addToast?.({ type: "error", message: "Error al actualizar notificaciones" });
        }
    };
}

function openDropdown(el) {
    el.classList.remove("hidden");
    setTimeout(() => {
        el.style.opacity = "1";
        el.style.transform = "scale(1)";
    }, 10);
}

function closeDropdown(el) {
    if (!el || el.classList.contains("hidden")) return;
    el.style.opacity = "0";
    el.style.transform = "scale(0.95)";
    setTimeout(() => {
        el.classList.add("hidden");
    }, 200);
}

async function refreshNotifications(root, { silent = false } = {}) {
    try {
        // Solo admin y manager tienen campana (correos y tareas del equipo).
        const role = await getUserRole();
        if (role !== "admin" && role !== "manager") return;
        const teamId = await getCurrentTeamId();
        if (!teamId) return;

        const [mails, activities] = await Promise.all([
            listUnreadEmailsByTeam(teamId),
            listPendingActivitiesByTeam(teamId)
        ]);

        const totalUnread = mails.length + activities.length;
        const dot = $(root, "#user-notif-dot");
        const badge = $(root, "#notif-count-badge");
        const list = $(root, "#notif-list");
        const notifDropdown = $(root, "#user-notif-dropdown");

        if (totalUnread > 0) {
            dot?.classList.remove("hidden");
            if (badge) badge.textContent = totalUnread;
        } else {
            dot?.classList.add("hidden");
            if (badge) badge.textContent = "0";
        }

        if (totalUnread === 0) {
            list.innerHTML = `
                <div class="p-8 text-center text-slate-400 text-xs">
                    <i class="fa-solid fa-check-circle text-2xl mb-2 text-emerald-400/50"></i>
                    <p>¡Todo al día!</p>
                </div>`;
            return;
        }

        // Render List
        let html = "";

        // Render Mails
        mails.forEach(m => {
            html += `
                <div class="p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer group" data-open-mail="${m.id}" data-event-id="${m.event_id}">
                    <div class="flex gap-3">
                        <div class="w-8 h-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center flex-shrink-0">
                            <i class="fa-solid fa-envelope text-xs"></i>
                        </div>
                        <div class="min-w-0 flex-1">
                            <div class="flex justify-between items-start mb-0.5">
                                <span class="text-[10px] font-bold text-blue-600 uppercase tracking-tighter">Nuevo Mail</span>
                                <span class="text-[9px] text-slate-400 capitalize">${new Date(m.created_at).toLocaleDateString("es-ES", { day: 'numeric', month: 'short' })}</span>
                            </div>
                            <div class="text-[11px] font-bold text-slate-900 group-hover:text-blue-600 truncate">${escapeHtml(m.subject)}</div>
                            <div class="text-[10px] text-slate-500 truncate mt-0.5">De: ${escapeHtml(m.contacts?.name || 'Cliente')} • Evento: ${escapeHtml(m.events?.client_name_legacy)}</div>
                        </div>
                    </div>
                </div>
            `;
        });

        // Render Activities
        activities.forEach(a => {
            const date = new Date(a.scheduled_date);
            const isOverdue = date < new Date();
            const colorClass = isOverdue ? "text-rose-600 bg-rose-50" : "text-amber-600 bg-amber-50";

            html += `
                <div class="p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer group" data-open-activity="${a.id}" data-event-id="${a.event_id}">
                    <div class="flex gap-3">
                        <div class="w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0">
                            <i class="fa-solid fa-clock text-xs"></i>
                        </div>
                        <div class="min-w-0 flex-1">
                            <div class="flex justify-between items-start mb-0.5">
                                <span class="text-[10px] font-bold ${isOverdue ? 'text-rose-600' : 'text-amber-600'} uppercase tracking-tighter">Actividad ${isOverdue ? 'Vencida' : 'Pendiente'}</span>
                                <span class="text-[9px] text-slate-400 capitalize">${date.toLocaleDateString("es-ES", { day: 'numeric', month: 'short' })}</span>
                            </div>
                            <div class="text-[11px] font-bold text-slate-900 group-hover:text-blue-600 truncate">${escapeHtml(a.subject)}</div>
                            <div class="text-[10px] text-slate-500 truncate mt-0.5">Evento: ${escapeHtml(a.event?.client_name_legacy || '—')} • Asiga. a: ${escapeHtml(a.assigned_profile?.display_name || 'Sin asignar')}</div>
                        </div>
                    </div>
                </div>
            `;
        });

        list.innerHTML = html;

        // Wire click interactions
        list.querySelectorAll("[data-open-mail]").forEach(el => {
            el.onclick = () => {
                const eventId = el.dataset.eventId;
                openEditEventModal(eventId, { onSaved: () => refreshNotifications(root) });
                closeDropdown(notifDropdown);
            };
        });

        list.querySelectorAll("[data-open-activity]").forEach(el => {
            el.onclick = () => {
                const activityId = el.dataset.openActivity;
                openEditActivityModal(activityId, { onSaved: () => refreshNotifications(root) });
                closeDropdown(notifDropdown);
            };
        });

    } catch (e) {
        console.warn("Error refreshing notifications:", e);
    }
}

async function syncUserData(root) {
    try {
        const { user, profile } = await getCurrentUserAndProfile();
        if (!user) return;

        const nameEl = $(root, "#user-display-name");
        const roleEl = $(root, "#user-role");
        const initialsEl = $(root, "#user-initials");

        const dName = profile?.display_name || user.email.split("@")[0];
        const role = profile?.role || "viewer";
        const initials = dName.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();

        refreshActiveNav().catch((e) => console.warn("Error refreshing navigation:", e));

        // Role display labels
        const roleLabels = { admin: 'ADMIN', manager: 'MANAGER', viewer: 'COLABORADOR', supplier: 'PROVEEDOR' };
        const roleTagLabels = { admin: 'Administrador', manager: 'Manager', viewer: 'Colaborador', supplier: 'Proveedor' };

        if (nameEl) nameEl.textContent = dName;
        if (roleEl) roleEl.textContent = roleLabels[role] || role.toUpperCase();
        if (initialsEl) initialsEl.textContent = initials;

        const ddName = $(root, "#user-dropdown-name");
        const ddEmail = $(root, "#user-dropdown-email");
        const ddRole = $(root, "#user-dropdown-role-tag");
        const ddInitials = $(root, "#user-dropdown-initials");

        // Show/Hide Admin Section
        const adminSection = $(root, "#user-admin-section");
        if (adminSection) {
            if (role === 'admin') {
                adminSection.classList.remove("hidden");
            } else {
                adminSection.classList.add("hidden");
            }
        }

        if (ddName) ddName.textContent = dName;
        if (ddEmail) ddEmail.textContent = user.email;
        if (ddRole) {
            ddRole.textContent = roleTagLabels[role] || role;
            if (role === 'admin') {
                ddRole.className = "px-1.5 py-0.5 bg-rose-100 text-[9px] font-bold text-rose-700 uppercase tracking-wider rounded";
            } else if (role === 'manager') {
                ddRole.className = "px-1.5 py-0.5 bg-amber-100 text-[9px] font-bold text-amber-700 uppercase tracking-wider rounded";
            } else {
                ddRole.className = "px-1.5 py-0.5 bg-blue-100 text-[9px] font-bold text-blue-600 uppercase tracking-wider rounded";
            }
        }
        if (ddInitials) ddInitials.textContent = initials;

        // Update sidebar settings button initials
        const sidebarInitials = document.querySelector("#sidebar-settings-initials");
        if (sidebarInitials) sidebarInitials.textContent = initials;

        const teamEl = $(root, "#user-team-name");
        if (teamEl) {
            const supabase = getSupabaseClient();
            const teamId = await getCurrentTeamId();
            if (teamId) {
                const { data } = await supabase.from("teams").select("name").eq("id", teamId).single();
                if (data) teamEl.textContent = data.name;
            }
        }

        // Hide notifications bell for viewers (no access to emails)
        const notifBtn = $(root, "#user-notif-btn");
        if (notifBtn && role !== 'admin' && role !== 'manager') {
            notifBtn.style.display = 'none';
        } else if (notifBtn) {
            notifBtn.style.display = '';
        }
    } catch (e) {
        console.warn("Error syncing user data:", e);
    }
}

function escapeHtml(str) {
    return String(str ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}
